import React, {FC, useCallback, useEffect, useState} from 'react';
import {useMap} from './useMap';
import {DeliveryType} from '../../../../../types/registerCardApplicationRequestPayload';
import {useDispatch, useSelector} from 'react-redux';
import {esiaRegistrationInfoSelector} from '../../../../../redux/selectors/login';
import {loginSlice} from '../../../../../redux/reducers/login';
import {ServerValidationError} from '../../../../../shared/models/serverValidationError';
import {addNotification} from '../../../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../../../shared/components/snackbar/model';
import {GetIssuePointCabinetResponsePayload} from '../../../../../shared/models/api/getIssuePointCabinetResponsePayload';
import {
  EsiaRegCardMapProps,
  initialYandexZoomValue,
  registerParentStoreActivationTokenReturnID,
  setCardDeliveryInfo,
  submitKidsDataAndSaveErrors,
} from './models';
import {mapFormValuesToEsiaRegData} from '../formMapper';
import {Screens} from '../../../../../shared/routing';
import {RegExps} from '../../../../../shared/constants/regexps';
import {getFormValues} from 'redux-form';
import {useRegCardState} from '../../esiaRegCardState/useRegCardState';
import {Forms} from '../../../../../shared/models/forms';
import {EsiaRegCardForm} from '../form/model';
import {holderProfileSelector} from '../../../../../redux/selectors/profile';
import {ShowResultModal} from '../modal/ShowResultModal';
import {useModal} from '../../../../../shared/components/modal';
import {EsiaRegCardMapWebLayout} from './web/EsiaRegCardMapWebLayout';
import {useIsDesktop} from '../../../../../shared/hooks/useIsDesktop';

const latOffset = 0.006;

export const EsiaRegCardMap: FC<EsiaRegCardMapProps> = ({onGoBack, navigation, closeRegStepComponent}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const {mapData, onPressMarker, currentMarker, centerPoint, markers} = useMap();
  const regInfo = useSelector(esiaRegistrationInfoSelector);
  const [russianPostDeliveryAddress, setRussianPostDeliveryAddress] = useState<string>('');
  const [isDeliveryPointSelected, setIsDeliveryPointSelected] = useState(false);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState(DeliveryType.IssuePoint);
  const [isRussianPostDeliveryAddressValid, setIsRussianPostDeliveryAddressValid] = useState(true);
  const [mapZoomLevel, setMapZoomLevel] = React.useState(initialYandexZoomValue);
  const [mapCenterPoint, setMapCenterPoint] = React.useState<[number, number]>(centerPoint);
  const [isIssuePointDetailsVisible, setIsIssuePointDetailsVisible] = useState(true);
  const {parent, confirmedKids} = useRegCardState();
  const profile = useSelector(holderProfileSelector());
  const isLogged = !!profile;
  const modals = useModal();
  const isDesktop = useIsDesktop();
  const formValues = useSelector((state) => {
    return getFormValues(Forms.esiaRegCardForm)(state) as EsiaRegCardForm;
  });
  const showModalProps = {
    modals,
    dispatch,
    isLogged,
    isDesktop,
    navigation,
    formValues,
  };

  useEffect(() => {
    if (selectedDeliveryType === DeliveryType.IssuePoint) setIsDeliveryPointSelected(currentMarker != null);
    else {
      setIsRussianPostDeliveryAddressValid(RegExps.regionRestriction.test(russianPostDeliveryAddress));
      setIsDeliveryPointSelected(!!russianPostDeliveryAddress);
    }
  }, [russianPostDeliveryAddress, currentMarker, selectedDeliveryType]);

  const onDeliveryTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDeliveryType(Number.parseInt((event.target as HTMLInputElement).value));
  };

  const onClientRegisterOrderPress = async () => {
    let parentAccountId: number | null = null;
    try {
      if (!regInfo) return;
      if (selectedDeliveryType === DeliveryType.IssuePoint && currentMarker?.id == null) return;
      setSubmitting(true);
      if (profile?.cardholderId) parentAccountId = profile.cardholderId;
      const submissionData = mapFormValuesToEsiaRegData(formValues, regInfo.token);
      const cardDeliveryInfo = setCardDeliveryInfo(selectedDeliveryType, currentMarker, russianPostDeliveryAddress);
      await dispatch(loginSlice.actions.setEsiaRegistrationInfo({...regInfo, cardDeliveryInfo}));
      const counterPartyId = await registerParentStoreActivationTokenReturnID(parent, submissionData, cardDeliveryInfo);
      parentAccountId = counterPartyId ? counterPartyId : parentAccountId;
      const errorsOnKidsRegistration = submitKidsDataAndSaveErrors(
        confirmedKids,
        formValues,
        regInfo,
        parentAccountId,
        cardDeliveryInfo,
      );
      if (errorsOnKidsRegistration.length > 0) {
        ShowResultModal({...showModalProps, message: errorsOnKidsRegistration});
      }
      if (parent?.checked || errorsOnKidsRegistration.length === 0) ShowResultModal(showModalProps);
    } catch (e) {
      const error = e as ServerValidationError;
      let errorText = error.data?.[0].message || error.message || 'Ошибка. Повторите еще раз';
      dispatch(
        addNotification({
          message: errorText,
          type: SnackbarNotificationType.Error,
        }),
      );
      ShowResultModal({...showModalProps, message: errorText});
    } finally {
      setSubmitting(false);
      closeRegStepComponent && closeRegStepComponent();
    }
  };

  const onIssuePointSelected = (issuePoint: GetIssuePointCabinetResponsePayload | null) => {
    if (issuePoint == null) return;
    onPressMarker(issuePoint);
    setIsIssuePointDetailsVisible(true);
    if (issuePoint != null && issuePoint.geolocation && issuePoint.geolocation.length === 2) {
      setMapZoomLevel(14);
      setMapCenterPoint([issuePoint.geolocation[0], issuePoint.geolocation[1] + latOffset]);
    }
  };

  useEffect(() => {
    if (currentMarker == null && mapData.length) {
      setMapZoomLevel(9);
      setMapCenterPoint(centerPoint);
    }
  }, [mapData]);

  const goBack = useCallback(() => {
    if (!navigation || isDesktop) return;
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate(Screens.Login);
    }
  }, [navigation]);

  return (
    <EsiaRegCardMapWebLayout
      selectedDeliveryType={selectedDeliveryType}
      onDeliveryTypeChange={onDeliveryTypeChange}
      mapData={mapData}
      onIssuePointSelected={onIssuePointSelected}
      currentMarker={currentMarker}
      mapCenterPoint={mapCenterPoint}
      mapZoomLevel={mapZoomLevel}
      isIssuePointDetailsVisible={isIssuePointDetailsVisible}
      setIsIssuePointDetailsVisible={setIsIssuePointDetailsVisible}
      markers={markers}
      russianPostDeliveryAddress={russianPostDeliveryAddress}
      isRussianPostDeliveryAddressValid={isRussianPostDeliveryAddressValid}
      setRussianPostDeliveryAddress={setRussianPostDeliveryAddress}
      onClientRegisterOrderPress={onClientRegisterOrderPress}
      submitting={submitting}
      isDeliveryPointSelected={isDeliveryPointSelected}
      goBack={goBack}
      onGoBack={onGoBack}
    />
  );
};
